'use no memo';

import {
  UploadedFileFormatIsNotAllowedBySpecifiedAcceptedField,
  showNotification,
} from 'platform/components';
import {Pattern, match} from 'ts-pattern';

import {useEffect, useRef, useState} from 'react';

import {
  useAddFilesToVehicleAlbumMutation,
  useDeletePhotoMutation,
  useDeleteVehicleAlbumMutation,
  useSetVehicleAlbumCoverPhotoMutation,
  useUnsetVehicleAlbumCoverPhotoMutation,
} from '@dms/api/sales';
import {UploadFileResponse} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {handleApiError} from '../utils/handleApiError';
import {UploadFileError, useCustomUpload} from './useCustomUpload';

interface UseVehicleAlbumProps {
  vehicleAlbumId: string;
  vehicleId: string;
}

export const useVehicleAlbum = (props: UseVehicleAlbumProps) => {
  const [processingImageIds, setProcessingImageIds] = useState<string[]>([]);
  const [uploadFile] = useCustomUpload();
  const [albumImagePreviews, setImagePreviews] = useState<{id: string; uri: string}[]>([]);
  const uploadQueue = useRef<Map<string, Promise<UploadFileResponse | undefined>>>(new Map());
  const albumImagePreviewsRef = useRef(albumImagePreviews);
  // eslint-disable-next-line react-hooks/refs
  albumImagePreviewsRef.current = albumImagePreviews;

  const [addFilesToVehicleAlbum, {isLoading: isAddFilesLoading}] =
    useAddFilesToVehicleAlbumMutation();
  const [deleteVehicleAlbum, {isLoading: isDeleteAlbumLoading}] = useDeleteVehicleAlbumMutation();
  const [deletePhoto, {isLoading: isDeletePhotoLoading}] = useDeletePhotoMutation();
  const [setAsCoverPhoto, {isLoading: isSetCoverLoading}] = useSetVehicleAlbumCoverPhotoMutation();
  const [unsetCoverPhoto, {isLoading: isUnsetCoverLoading}] =
    useUnsetVehicleAlbumCoverPhotoMutation();

  const showFilePreview = (file: string | File) => {
    if (typeof file === 'string') {
      setImagePreviews((prev) => [...prev, {id: file, uri: file}]);
    } else {
      setImagePreviews((prev) => [...prev, {id: file.name, uri: URL.createObjectURL(file)}]);
    }
  };

  const hideFilePreview = (fileName: string) => {
    setImagePreviews((prev) => {
      const preview = prev.find((item) => item.id === fileName);
      if (preview) {
        URL.revokeObjectURL(preview.uri);
      }
      return prev.filter((item) => item.id !== fileName);
    });
  };

  useEffect(
    () => () => {
      albumImagePreviewsRef.current.forEach((preview) => URL.revokeObjectURL(preview.uri));
    },
    []
  );

  const {vehicleAlbumId, vehicleId} = props;
  useEffect(() => {
    if (uploadQueue.current.size === 0 || uploadQueue.current.size !== albumImagePreviews.length) {
      return;
    }
    const previewsSnapshot = albumImagePreviews;
    (async () => {
      const settled = await Promise.all(
        previewsSnapshot.map(async (preview) => {
          const uploadPromise = uploadQueue.current.get(preview.id);
          uploadQueue.current.delete(preview.id);
          const data = uploadPromise ? await uploadPromise.catch(() => undefined) : undefined;
          hideFilePreview(preview.id);
          return data?.fileId;
        })
      );
      const fileIds = settled.filter((id): id is string => typeof id === 'string');
      if (fileIds.length === 0) {
        return;
      }
      addFilesToVehicleAlbum({
        vehicleAlbumId,
        vehicleId,
        addFilesToVehicleAlbumRequestBody: {fileIds},
      })
        .unwrap()
        .catch(handleApiError);
    })();
  }, [albumImagePreviews, addFilesToVehicleAlbum, vehicleAlbumId, vehicleId]);

  const startProcessingImage = (id: string | string[]) => {
    setProcessingImageIds((prev) => [...prev, ...(Array.isArray(id) ? id : [id])]);
  };

  const stopProcessingImage = (id: string | string[]) => {
    setProcessingImageIds((prev) =>
      prev.filter((imageId) => (Array.isArray(id) ? !id.includes(imageId) : id !== imageId))
    );
  };

  const addImageToAlbum = ({
    file,
    onSuccess,
    onError,
  }: {
    file: File;
    onSuccess?: (data: unknown) => void;
    onError?: (error: UploadFileError | ProgressEvent) => void;
  }) => {
    const uploadPromise = new Promise<UploadFileResponse | undefined>((resolve, reject) => {
      uploadFile({
        file,
        onError: (error) => {
          onError?.(error);
          reject();
        },
        onSuccess: (data) => {
          resolve(data);
          onSuccess?.(data);
        },
      });
    });

    uploadQueue.current.set(file.name, uploadPromise);
  };

  const deleteImageFromAlbum = (uuid: string | string[]): Promise<boolean | void> => {
    if (!uuid || (Array.isArray(uuid) && !uuid.length)) {
      throw new Error('No file id provided to delete');
    }
    startProcessingImage(uuid);
    return deletePhoto({
      vehicleId: props.vehicleId,
      albumId: props.vehicleAlbumId,
      vehiclePhotoDeleteRequestBody: {
        photos: Array.isArray(uuid) ? uuid.map((fileId) => ({fileId})) : [{fileId: uuid}],
      },
    })
      .unwrap()
      .then(() => true)
      .catch(handleApiError)
      .finally(() => stopProcessingImage(uuid));
  };

  const deleteAlbum = (): Promise<boolean | void> =>
    deleteVehicleAlbum({vehicleAlbumId: props.vehicleAlbumId})
      .unwrap()
      .then(() => true)
      .catch(handleApiError);

  const setImageAsCoverPhoto = (vehiclePhotoId: string): Promise<boolean | void> => {
    startProcessingImage(vehiclePhotoId);
    return setAsCoverPhoto({
      vehicleAlbumId: props.vehicleAlbumId,
      vehiclePhotoId,
      vehicleId: props.vehicleId,
    })
      .unwrap()
      .then(() => true)
      .catch(handleApiError)
      .finally(() => stopProcessingImage(vehiclePhotoId));
  };

  const unsetImageAsCoverPhoto = (): Promise<boolean | void> =>
    unsetCoverPhoto({
      vehicleAlbumId: props.vehicleAlbumId,
      vehicleId: props.vehicleId,
    })
      .unwrap()
      .then(() => true)
      .catch(handleApiError);

  const handleUploadError = (error: Error | string, _ret: Record<string, unknown>, file: File) => {
    match({error})
      .with(
        {error: Pattern.instanceOf(UploadedFileFormatIsNotAllowedBySpecifiedAcceptedField)},
        () => {
          showNotification.error(
            i18n.t('entity.photo.notifications.imageFormatIsNotAllowed', {
              name: file.name,
              mimeFormat: file.type,
            })
          );
        }
      )
      .otherwise(() => {
        showNotification.error(
          i18n.t('entity.photo.notifications.uploadFailed', {name: file.name})
        );
      });
  };

  return {
    actions: {
      addImageToAlbum,
      deleteImageFromAlbum,
      deleteAlbum,
      setImageAsCoverPhoto,
      unsetImageAsCoverPhoto,
    },
    handlers: {handleUploadError},
    filePreview: {
      list: () => albumImagePreviews,
      show: showFilePreview,
      hide: hideFilePreview,
    },
    processingImageIds,
    areActionsInProgress:
      isAddFilesLoading ||
      isDeleteAlbumLoading ||
      isDeletePhotoLoading ||
      isSetCoverLoading ||
      isUnsetCoverLoading,
  };
};
